import React, { useEffect, useRef, useState } from 'react';
import { ComponentUpdateProps, ComponentSettingsLlm, TextBlockInitialDataResponse } from '@report-content';
import useReportContentContext from 'components/report-content/hooks/useReportContentContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AiInsightCanvasScope, reportAPI } from 'api/report';
import { LlmInitialDataResponse } from 'components/report-content/src/model/cmp/llm';
import { renderMarkdown } from 'tools/safeHtml';
import useOnSVChange from 'components/report-content/hooks/useOnSVChange';
import useOnMeasurementTime from 'components/report-content/hooks/useOnMeasurementTime';
import useComponentReady from 'components/report-content/hooks/useComponentReady';
import useBundleTranslation from 'i18n';
import { Box, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';

function LlmRow({ summary, details }: { summary?: string; details?: string }) {
    const { t } = useBundleTranslation('main');
    const [showDetails, setShowDetails] = useState(false);

    return (
        <Box>
            {summary && (
                <Box
                    dangerouslySetInnerHTML={{
                        __html: renderMarkdown(summary),
                    }}
                />
            )}
            {details && (
                <Box>
                    <Box className={'invert-link'} component={'span'} onClick={() => setShowDetails(!showDetails)}>
                        {t(showDetails ? 'hide_details' : 'show_details')}
                    </Box>
                </Box>
            )}
            {showDetails && details && (
                <Box
                    dangerouslySetInnerHTML={{
                        __html: renderMarkdown(details),
                    }}
                />
            )}
        </Box>
    );
}

export default function Llm({
    contentSettings,
    component,
    updateBlockSettings,
}: ComponentUpdateProps<ComponentSettingsLlm>) {
    const { t } = useBundleTranslation(['components/report-content']);
    const { filtersString } = useReportContentContext();
    const queryClient = useQueryClient();
    const initialQueryKey = `report_block_initial_${component.datasetRendererBlockComponentId}`;
    const removeInitialData = () => queryClient.removeQueries({ queryKey: [initialQueryKey] });
    const isEditor = contentSettings.context === 'edit';
    // no key = a block saved before the picker existed: the stored rows apply, '' = a cleared picker
    const hasPickerKey = component.settings.primary_resource !== undefined;
    const pickerScope = (): AiInsightCanvasScope => ({
        primaryResource: component.settings.primary_resource ?? '',
        resources: component.settings.resources ?? [],
    });
    // Posted as the preview posts them: an unsaved block from its first fetch on ('' = cleared; no key = pre-picker
    // block, the seed applies), a saved block only after an Apply so its mirror never overrides the stored rows.
    const appliedScope = useRef<AiInsightCanvasScope | null>(
        isEditor && !component.settings.report_ai_insight_id && hasPickerKey ? pickerScope() : null,
    );
    const { data: initialData, isLoading } = useQuery<LlmInitialDataResponse, Error>({
        queryKey: [initialQueryKey, filtersString],
        queryFn: () => {
            return reportAPI.getComponentData(
                component.settings,
                contentSettings,
                filtersString,
                appliedScope.current ?? undefined,
            );
        },
    });

    // Reset all data on segment value change
    useOnSVChange(contentSettings.segmentValueId, removeInitialData);
    // Full reset on Measurement Interval Change
    useOnMeasurementTime(contentSettings.measurement_time, removeInitialData);

    const settingsKey = JSON.stringify([
        component.settings.prompt,
        component.settings.primary_resource ?? null,
        component.settings.resources ?? [],
    ]);
    const lastSettingsKey = useRef<string | null>(null);
    useEffect(() => {
        if (lastSettingsKey.current === settingsKey) {
            return;
        }
        const isMount = lastSettingsKey.current === null;
        lastSettingsKey.current = settingsKey;
        if (isMount) {
            return;
        }
        if (isEditor && hasPickerKey) {
            appliedScope.current = pickerScope();
        }
        // not removeQueries: a fetch of the previous settings still in flight must be cancelled
        queryClient.resetQueries({ queryKey: [initialQueryKey] });
    }, [settingsKey]);

    useComponentReady(initialData);

    const isFailed = !initialData || initialData.ai.status === 'failure';

    useEffect(() => {
        if (isLoading || isFailed === !!component.settings.requiresConfiguration) {
            return;
        }
        updateBlockSettings({ ...component.settings, requiresConfiguration: isFailed }, { shouldDirty: false });
    }, [isLoading, isFailed]);

    return (
        <Box sx={{ px: 1.5, py: 1, '& p': { mx: 0, my: 0.5 } }}>
            {isLoading ? (
                <Box>{t('llm.ai_analysis_is_running___')}</Box>
            ) : isFailed ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'error.main' }}>
                    <IconMi icon="alert" fontSize="16" />
                    <Box>{t('llm.generation_failed')}</Box>
                </Stack>
            ) : (
                <Box>
                    {initialData.ai.result.map((row, key) => (
                        <LlmRow key={`llm_row_{$key}`} summary={row.summary} details={row.details} />
                    ))}
                </Box>
            )}
        </Box>
    );
}
